import {
  Capability,
  capabilityManager,
  dataContext,
  EventBus,
  FSRef,
  GitWorkdir,
  type JourneyActSpec,
  oauthService,
} from '@sdk';

import { getGitStatus } from '@src/lib/git-status-cache';
import { LOCAL_COMPUTE_NODE } from '@src/navigation/asset-doc-types';
import { runInTerminal } from '@src/terminal/run-in-terminal';

/** A step's act landed / could not land. The step's `await` listens for these
 *  like any other bus event — gating stays ONE mechanism (see docs/tags.md). */
export const ACT_DONE_TAG = 'app.journey.act.done';
export const ACT_FAILED_TAG = 'app.journey.act.failed';

/** Bus target for an act: `<kind>:<tag word>` — e.g. `fill:AgentInstructions`. */
export function actTarget(kind: string, target: string): string {
  return `${kind}:${target}`;
}

/**
 * The editable the act should type into: the tagged element itself when it IS
 * one, else the first editable inside it. A journey tags the CONTAINER (the
 * editor pane), not the inner surface — which for a rich editor is a
 * ProseMirror node the component owns and may re-create.
 */
function editableWithin(host: HTMLElement): HTMLElement | null {
  const isEditable = (el: HTMLElement) =>
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    el.isContentEditable;
  if (isEditable(host)) return host;
  return host.querySelector<HTMLElement>('input, textarea, [contenteditable="true"]');
}

/**
 * Press a `data-tag`-tagged surface, as a user would.
 *
 * The tag is not always ON the control: a group like `ViewToggle` tags a wrapper
 * and holds the real buttons inside, so clicking the host would hit a div and do
 * nothing. The control is resolved the same way {@link performFill} resolves an
 * editable — the host itself when it is clickable, else the first clickable
 * descendant.
 *
 * `el.click()` dispatches a real click event, which is what React's synthetic
 * handlers listen for — the same path a mouse takes.
 *
 * Returns false when the target isn't on screen, so the caller emits
 * `app.journey.act.failed` and the step falls back to asking the user.
 */
export function performClick(target: string): boolean {
  const host = document.querySelector<HTMLElement>(`[data-tag="${CSS.escape(target)}"]`);
  if (!host) return false;
  const el = host.matches(CLICKABLE) ? host : host.querySelector<HTMLElement>(CLICKABLE);
  if (!el) return false;
  el.click();
  return true;
}

/** What counts as the control inside a tagged host. */
const CLICKABLE = 'button, a[href], [role="button"], input[type="button"], input[type="submit"]';

/**
 * Type `text` into a `data-tag`-tagged surface, as a user would.
 *
 * Inputs/textareas are set through the NATIVE value setter + an `input` event,
 * because React installs its own value property on the instance and a plain
 * `el.value = …` is invisible to it. Rich editors (the agent instructions are a
 * Milkdown/ProseMirror doc) are fed through `insertText`, the same
 * `beforeinput` path a keystroke or a paste takes — so the editor's own parsing,
 * undo history and change events all run. Poking their DOM directly would
 * desync the document model.
 *
 * Returns false when the target isn't on screen or can't be typed into; the
 * caller emits `app.journey.act.failed` so the step can fall back to asking the
 * user to type it themselves.
 */
export function performFill(target: string, text: string): boolean {
  const host = document.querySelector<HTMLElement>(`[data-tag="${CSS.escape(target)}"]`);
  const el = host ? editableWithin(host) : null;
  if (!el) return false;

  el.focus();
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement : HTMLInputElement;
    // eslint-disable-next-line @typescript-eslint/unbound-method -- invoked via .call below
    const setter = Object.getOwnPropertyDescriptor(proto.prototype, 'value')?.set;
    if (!setter) return false;
    setter.call(el, text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  // contenteditable: place the caret at the end, then insert as real input.
  const sel = window.getSelection();
  if (sel) {
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    sel.removeAllRanges();
    sel.addRange(range);
  }
  return document.execCommand('insertText', false, text);
}

function announce(act: { kind: string; target: string }, ok: boolean): boolean {
  EventBus.emit(
    ok ? ACT_DONE_TAG : ACT_FAILED_TAG,
    actTarget(act.kind, act.target),
    { act },
    { origin: 'app' },
  );
  return ok;
}

/** The capability row an act aims at (exact kind), or null. */
async function capabilityRow(kind: string | undefined): Promise<Capability | null> {
  if (!kind) return null;
  await capabilityManager.load();
  return capabilityManager.getAll().find((c) => c.kind === kind) ?? null;
}

/**
 * The async act kinds drive the capability system through its existing verbs.
 * "Done" here means the flow STARTED (install process spawned, OAuth window
 * opened, login session live) — the step's `await` gates on the capability
 * row actually reaching the wanted state.
 */
async function runSetupAct(act: JourneyActSpec): Promise<boolean> {
  try {
    if (act.kind === 'setup_capability') {
      if (!act.capability) return announce(act, false);
      await capabilityManager.setup(act.capability);
      return announce(act, true);
    }
    if (act.kind === 'oauth_connect') {
      await oauthService.connect(act.provider ?? 'github');
      return announce(act, true);
    }
    if (act.kind === 'device_login') {
      const row = await capabilityRow(act.capability);
      if (!row) return announce(act, false);
      // Progress (URL + one-time code) arrives on the row's login_* fields
      // over WS — the tray renders them for device_login steps.
      await row.deviceLogin();
      return announce(act, true);
    }
    return announce(act, false);
  } catch {
    return announce(act, false);
  }
}

/** A path inside the journey's project. The journey's own root wins; the
 *  active project is the fallback for acts raised outside one. `rel` is
 *  optional: no `rel` is the root itself. */
function projectPath(rel: string | undefined, ctx: ActContext): string | null {
  const base = ctx.projectRoot === undefined
    ? dataContext.project?.fs_storage_mount_path ?? dataContext.workdir
    : ctx.projectRoot;
  if (!base) return null;
  return rel ? `${base.replace(/\/+$/, '')}/${rel.replace(/^\/+/, '')}` : base;
}

/**
 * Verify the working tree against REAL git state — the step is done only when
 * the repo says so (event ≠ proof: the user's terminal commands are invisible
 * to the bus; the repo is the truth). All probes go through the compute node's
 * `git-ops` action, the same backend the footer git pill reads.
 *
 * The status probe goes through the SHARED `getGitStatus` cache the footer pill
 * uses, always `force`d: a Check is an explicit "did my command land?", so it
 * must never answer from the TTL — but it still single-flights against a
 * concurrent pill fetch, and refreshing the shared entry means the footer count
 * agrees with what the journey just verified.
 */
async function runGitCheck(act: JourneyActSpec, ctx: ActContext): Promise<boolean> {
  try {
    const workdir = projectPath(act.dir, ctx);
    if (!workdir) return announce(act, false);
    const computeNodeId = dataContext.computeNodeTypeId?.id ?? LOCAL_COMPUTE_NODE.id;
    const git = new GitWorkdir(workdir, computeNodeId);
    switch (act.expect) {
      case 'repo':
        return announce(act, await git.isInit());
      case 'branch':
        return announce(act, !!act.branch && (await git.getBranch()) === act.branch);
      case 'staged':
      case 'clean':
      case 'dirty': {
        const status = await getGitStatus(computeNodeId, workdir, { force: true });
        if (!status || status.error) return announce(act, false);
        if (act.expect === 'staged') return announce(act, status.files.some((f) => f.staged));
        if (act.expect === 'dirty') return announce(act, status.files.length > 0);
        return announce(act, status.files.length === 0 && (await git.hasCommit()));
      }
      default:
        return announce(act, false);
    }
  } catch {
    return announce(act, false);
  }
}

/**
 * What the manager lends an act that needs app powers it cannot reach from a
 * pure module: opening a terminal, and the id of the one currently on screen.
 */
export interface ActContext {
  /** Create + open a terminal in the current project; resolves its shell id. */
  openTerminal?: () => Promise<string | null>;
  /** The shell the current dock is showing, if any — where `run` types. */
  shellId?: string | null;
  /** Aborted when the step changes or the tray unmounts, so a watcher waiting
   *  on output lets go instead of outliving the step that started it. */
  signal?: AbortSignal;
  /** The project the JOURNEY ships in — where its try-it steps run. Not the
   *  active project: a tour that says "the repo you're in IS syncmd" must open
   *  its terminal and read its files THERE, whatever the user has open. */
  projectRoot?: string | null;
}

/**
 * Type the step's command into the terminal on screen, asserting its output
 * when the step says `contains`.
 *
 * The mechanics live in `runInTerminal` — a terminal is not journey-private,
 * and an agent driving it through `flow terminal` must behave identically. This
 * is only the journey's adapter: shell identity from the context, verdict onto
 * the bus.
 */
async function runShellCommand(act: JourneyActSpec, ctx: ActContext): Promise<boolean> {
  try {
    // The terminal on screen IS the target, and the navigation layer owns
    // "what am I looking at" — never re-parse the address bar here.
    if (!ctx.shellId || !act.command) return announce(act, false);
    const ok = await runInTerminal(ctx.shellId, act.command, {
      contains: act.contains,
      signal: ctx.signal,
    });
    return announce(act, ok);
  } catch {
    return announce(act, false);
  }
}

/** Open a real terminal in this project — the same path the Terminal tile takes. */
async function runOpenTerminal(act: JourneyActSpec, ctx: ActContext): Promise<boolean> {
  try {
    const shellId = ctx.openTerminal ? await ctx.openTerminal() : null;
    return announce(act, !!shellId);
  } catch {
    return announce(act, false);
  }
}

/** The project-relative file an `fs_check` act probes. */
function projectFile(path: string, ctx: ActContext): FSRef | null {
  const full = projectPath(path, ctx);
  return full ? new FSRef(full, dataContext.computeNodeTypeId ?? LOCAL_COMPUTE_NODE) : null;
}

/**
 * Prove a file is really there (and optionally that it carries `contains`) —
 * the filesystem is the truth. The user's terminal commands never reach the
 * bus, so "they said they ran it" is not evidence; this reads the file back.
 */
async function runFsCheck(act: JourneyActSpec, ctx: ActContext): Promise<boolean> {
  try {
    const ref = projectFile(act.path ?? '', ctx);
    if (!ref || !act.path) return announce(act, false);
    if (!act.contains) return announce(act, await ref.exists());
    const body = await ref.read().catch(() => null);
    return announce(act, !!body && body.includes(act.contains));
  } catch {
    return announce(act, false);
  }
}

/** Run a step's act and announce the outcome on the bus. */
export function runAct(act: JourneyActSpec, ctx: ActContext = {}): boolean | Promise<boolean> {
  switch (act.kind) {
    case 'click':
      return announce(act, performClick(act.target));
    case 'fill':
      return announce(act, performFill(act.target, act.text ?? ''));
    case 'open_terminal':
      return runOpenTerminal(act, ctx);
    case 'run':
      return runShellCommand(act, ctx);
    case 'fs_check':
      return runFsCheck(act, ctx);
    case 'git_check':
      return runGitCheck(act, ctx);
    default:
      return runSetupAct(act);
  }
}
